import { useCallback, useEffect, useMemo, useState } from 'react'
import { AnyAction } from '@reduxjs/toolkit'
import { useLocation, useNavigate } from 'react-router-dom'
import { Close } from '@mui/icons-material'
import Modal, { ModalProps } from '@mui/material/Modal'

import { IErrorData, IErrorPayload, IFilterProfile } from '$/types'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import {
  useAddSystemScopeFilterProfileMutation,
  useDeleteSystemScopeFilterProfileMutation,
  useGetActiveSystemScopeFilterProfileQuery,
  useLazyGetSystemScopeFilterProfileQuery,
  useUpdateSystemScopeFilterProfileMutation
} from 'app/services/api/filterprofiles'
import { useGetCurrentUserIdQuery } from 'app/services/api/users'
import { denormalizedFilterProfilesSelectors } from 'app/entities/store/filterProfilesSelectors'
import { deleteFilterProfileSync } from 'app/entities/store/filterProfilesSlice'
import LineSelector from 'app/layout/Navbar/Filters/FilterModal/LineSelector'
import ProfileDetails from 'app/layout/Navbar/Filters/FilterModal/ProfileDetails'
import ProfileSelector from 'app/layout/Navbar/Filters/FilterModal/ProfileSelector'
import {
  selectEditInProgress,
  selectStagingProfile,
  selectStagingProfileAPIRequestStatus,
  selectStagingProfileValidationErrors
} from 'app/layout/Navbar/Filters/globalFilterSelectors'
import {
  setEditInProgressSync,
  setStagingProfileFulfilledSync,
  setStagingProfileRejectedSync,
  setStagingProfileSync,
  setStagingProfileValidationErrorsSync,
  setSystemScopeIdSync
} from 'app/layout/Navbar/Filters/globalFilterSlice'
import LoadingSpinner from 'common/components/LoadingSpinner'
import {
  getDefaultProfileId,
  getUnsavedProfile
} from 'common/utils/filter/systemScope'
import { qraSystemGraphsViewRedirectHelper } from 'common/utils/navigation/qraNavUtils'

import { EMPTY_LOCAL_FILTER } from 'features/RiskManagement/Components/LocalFilter'
import { setSavedFilterSync } from 'features/RiskManagement/Components/LocalFilter/localFilterSlice'

import {
  LOCAL_PROFILE_ID,
  NEW_PROFILE_NAME,
  UNSAVED_PROFILE_NAME
} from './constants'

interface IFilterModal {
  onClose: () => void
  isIncludingRoutes: boolean
}

const FilterModal = ({ onClose, isIncludingRoutes }: IFilterModal) => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()

  /**
   * NOTE: active system scope filter profile refetch is necessary here
   * because we have not yet wire up mutation calls (for invalidation).
   */

  const [
    addSystemScopeFilterProfile,
    {
      isLoading: isCreatingProfile,
      isSuccess: isProfileCreateSuccess,
      isError: isProfileCreateRejected,
      error: createError
    }
  ] = useAddSystemScopeFilterProfileMutation()

  const [
    updateSystemscopeFilterProfile,
    {
      isLoading: isUpdatingProfile,
      isSuccess: isProfileUpdateSuccess,
      isError: isProfileUpdateRejected,
      error: updateError
    }
  ] = useUpdateSystemScopeFilterProfileMutation()

  const [deleteFilterProfile] = useDeleteSystemScopeFilterProfileMutation()

  const { systemScopeFilterProfile, isFetching: isActiveProfileLoading } =
    useGetActiveSystemScopeFilterProfileQuery()

  const [
    lazyGetSystemScopeFilterProfile,
    { isFetching: isStagingProfileLoading }
  ] = useLazyGetSystemScopeFilterProfileQuery()

  const { currentData: currentUserId } = useGetCurrentUserIdQuery()

  const filterProfiles = useAppSelector(
    denormalizedFilterProfilesSelectors.selectAll
  )

  const stagingProfile = useAppSelector(selectStagingProfile)
  const editInProgress = useAppSelector(selectEditInProgress)

  const stagingProfileValidationErrors = useAppSelector(
    selectStagingProfileValidationErrors
  )

  const stagingProfileAPIRequestStatus = useAppSelector(
    selectStagingProfileAPIRequestStatus
  )

  const [actionBarText, setActionBarText] = useState<string>('')
  const [searchValue, setSearchValue] = useState<string>('')
  const [onlyShowSelected, setOnlyShowSelected] = useState<boolean>(false)
  const [initiallyPrivate, setInitiallyPrivate] = useState<boolean>(true)
  const [isLoadingContinuousRoutes, setIsLoadingContinuousRoutes] =
    useState(false)

  const isLineSelectorLoading =
    isIncludingRoutes ||
    isLoadingContinuousRoutes ||
    isActiveProfileLoading ||
    isStagingProfileLoading

  const [
    latestNewProfileSelectionTimestamp,
    setLatestNewProfileSelectionTimestamp
  ] = useState<number>(-1)

  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false)

  const profileTemplateFields: IFilterProfile = useMemo(() => {
    return {
      id: LOCAL_PROFILE_ID,
      name: 'Profile Template',
      userId: currentUserId || 'uninitialized',
      isPrivate: true,
      isActive: true,
      operators: [],
      states: [],
      regions: [],
      customQuery: '',
      customQueryString: '',
      continuousRoutes: [],
      isDefault: false
    }
  }, [currentUserId])

  const getSystemScopeFilterProfile = (
    id: string,
    requestFulfilled: (payload: IFilterProfile | undefined) => AnyAction,
    requestRejected: (payload: IErrorPayload | undefined) => AnyAction
  ) => {
    lazyGetSystemScopeFilterProfile(id)
      .unwrap()
      .then((result) => dispatch(requestFulfilled(result)))
      .catch((error) => dispatch(requestRejected(error)))
  }

  const newProfileTemplate: IFilterProfile = useMemo(() => {
    return {
      ...profileTemplateFields,
      name: NEW_PROFILE_NAME
    }
  }, [profileTemplateFields])

  /**
   * Reset validation/status fields used in other system scope filter modal components
   */

  const resetWhenSaving = useCallback((): void => {
    // Network / Line Selector
    setOnlyShowSelected(false)
    setSearchValue('')

    dispatch(setEditInProgressSync(false))
    dispatch(setStagingProfileValidationErrorsSync({}))
  }, [dispatch])

  const resetValidationStatus = useCallback((): void => {
    resetWhenSaving()

    // Action Bar
    setActionBarText('')
  }, [resetWhenSaving])

  /**
   * Actions to execute after successful Apply operation
   * NOTE: code may execute before dependant dispatches complete
   */
  const profileSaveSuccessCallback = useCallback(
    (profile: IFilterProfile | undefined): void => {
      // update staging profile
      dispatch(setStagingProfileFulfilledSync(profile))

      // update system scope ID
      dispatch(setSystemScopeIdSync(profile?.id))

      // Reset Local Filter
      dispatch(setSavedFilterSync(EMPTY_LOCAL_FILTER))

      // QRA Specific Redirect Logic
      qraSystemGraphsViewRedirectHelper(location, navigate)

      resetWhenSaving()
    },
    [dispatch, location, navigate, resetWhenSaving]
  )

  // #region profileApplySuccessCallback
  const profileApplySuccessCallback = (
    profile: IFilterProfile | undefined
  ): void => {
    profileSaveSuccessCallback(profile)
    setActionBarText('')
    onClose()
  }
  // #endRegion

  const closeModal = useCallback(() => {
    onClose()
    dispatch(setStagingProfileSync(systemScopeFilterProfile))
    resetValidationStatus()
  }, [dispatch, onClose, resetValidationStatus, systemScopeFilterProfile])

  const dismissModal = useCallback<NonNullable<ModalProps['onClose']>>(
    (_, reason) => {
      if (reason === 'backdropClick') {
        return
      }

      closeModal()
    },
    [closeModal]
  )

  useEffect(() => {
    if (editInProgress) {
      setActionBarText('Profile contains unsaved changes')
    }
  }, [editInProgress])

  useEffect(() => {
    /**
     * REVIEW: This runs after RTKQ unwrap.
     * Such that it may be the status code that always determines the message..
     */
    if (isUpdatingProfile || isCreatingProfile) {
      setActionBarText('Saving...')
    } else if (isProfileUpdateSuccess || isProfileCreateSuccess) {
      setActionBarText('Profile saved successfully')
    } else if (isProfileUpdateRejected || isProfileCreateRejected) {
      const err = (updateError ?? createError) as {
        status?: number
        data?: {
          errors: {
            code: number
            detail: string
          }[]
        }
      }

      if (err?.status === 403) {
        setActionBarText('')
      } else if (
        err?.status === 422 &&
        err.data?.errors.find((err) => err.code === 704)
      ) {
        setActionBarText('Profile name is already in use')
      } else {
        setActionBarText('Profile save error')
      }
    }
  }, [
    isCreatingProfile,
    isUpdatingProfile,
    isProfileCreateSuccess,
    isProfileUpdateSuccess,
    isProfileCreateRejected,
    isProfileUpdateRejected,
    createError,
    updateError
  ])

  /**
   * Begin non hook functions
   */

  /**
   * System scope filter profile validation object helper
   * REVIEW: move this into a utils method?
   */
  const hasValidationErrors = (): boolean => {
    for (const isError of Object.values(stagingProfileValidationErrors)) {
      if (isError as boolean) {
        return true
      }
    }

    return false
  }

  // #region Profile Selector callbacks
  const addProfileCallback = (): void => {
    resetValidationStatus()
    // NOTE: Creation of a profile falls under the edit action
    dispatch(setEditInProgressSync(true))
    dispatch(setStagingProfileSync(newProfileTemplate))
    setLatestNewProfileSelectionTimestamp(Date.now())
  }

  const selectProfileCallback = (key: string): void => {
    const selectedProfile = filterProfiles.find((p) => p.id.toString() === key)

    if (selectedProfile != null) {
      resetValidationStatus()

      getSystemScopeFilterProfile(
        key,
        setStagingProfileFulfilledSync,
        setStagingProfileRejectedSync
      )
    }
  }

  const deleteProfileCallback = (id: string): void => {
    /**
     * If profile to delete is the same as current active profile
     * then reset current profile to use the "default" profile
     */
    if (systemScopeFilterProfile?.id.toString() === id.toString()) {
      const defaultProfileId = getDefaultProfileId(filterProfiles)
      const defaultProfile = filterProfiles.find(
        (fp) => fp.id === defaultProfileId
      )

      if (defaultProfile != null) {
        /**
         * Simulate `profileSaveSuccessCallback(defaultProfile)`
         * but w/o closing the modal
         */

        // update system scope ID globally
        dispatch(setSystemScopeIdSync(defaultProfile.id))
        // Reset Local Filter
        dispatch(setSavedFilterSync(EMPTY_LOCAL_FILTER))
        // QRA Specific Redirect Logic
        qraSystemGraphsViewRedirectHelper(location, navigate)
        resetWhenSaving()
      } else {
        console.warn('Default system scope not found')
      }
    }

    deleteFilterProfile(id)
      .unwrap()
      .then(() => {
        dispatch(deleteFilterProfileSync(id))
      })
  }
  // #endregion

  // #region Action Bar component callbacks
  const resetProfileCallback = (): void => {
    resetValidationStatus()

    if (stagingProfile != null) {
      if (stagingProfile.id === LOCAL_PROFILE_ID) {
        /**
         * NOTE: This can either be a new profile or an unsaved profile
         *       if it did not exist beforehand. But in this scenario
         *       we would want user to create a new profile anyway since
         *       unsaved profile is for fallback.
         */
        dispatch(setStagingProfileSync(newProfileTemplate))
        setLatestNewProfileSelectionTimestamp(Date.now())
      } else {
        getSystemScopeFilterProfile(
          stagingProfile.id.toString(),
          setStagingProfileFulfilledSync,
          setStagingProfileRejectedSync
        )
      }
    }
  }

  const onSaveProfile = (): void => {
    if (!(stagingProfile?.isPrivate ?? false)) {
      setShowConfirmationModal(true)
    } else {
      saveProfileCallback()
    }
  }

  /**
   * We have switched to using RTKQ to make the API calls but are still
   * manually dispatching actions to old API reducers to be compatible
   * with logic in that reducer.
   *
   * extra dispatches needed for compatibility .
   * In particular the use of `setSaveProfileAPIRequestStatusTracker`
   */
  // TODO: [FE DEBT] Refactoring reducer to remove the need of having many of
  const updateProfileAPICallWrapper = (
    stagingProfile: IFilterProfile
  ): void => {
    updateSystemscopeFilterProfile(stagingProfile)
      .unwrap()
      .then((resProfile) => {
        dispatch(setStagingProfileFulfilledSync(resProfile))
        // setActionText currently handled by ^ handlers
      })
      .catch((err) => {
        const errorPayload = {
          response: {
            errors: [
              {
                title: err.status,
                detail: err.error,
                source: {
                  pointer: 'Update Profile'
                }
              }
            ] as IErrorData[]
          }
        }

        dispatch(setStagingProfileRejectedSync(errorPayload))
        dispatch(setEditInProgressSync(true))
      })
  }

  // #region saveProfileCallback
  const saveProfileCallback = async (): Promise<void> => {
    if (stagingProfile == null) {
      console.warn('Save occured while SP is nullish')

      return
    }

    if (hasValidationErrors()) {
      console.warn('System scope validation error detected on save')

      return
    }

    const unsavedProfileId = getUnsavedProfile(filterProfiles)?.id

    dispatch(setEditInProgressSync(false))

    if (
      stagingProfile.name.trim().toLowerCase() ===
      UNSAVED_PROFILE_NAME.toLowerCase()
    ) {
      /**
       * Name validation should prevent submittion of profile that is user
       * renamed to `UNSAVED_PROFILE_NAME` so we can assume this is update
       * of existing unsaved profile.
       */
      updateProfileAPICallWrapper(stagingProfile)
    } else if (
      [LOCAL_PROFILE_ID, unsavedProfileId].includes(stagingProfile.id)
    ) {
      // CREATE from "new" or renamed "unsaved profile"

      const newStagingProfile = {
        ...stagingProfile,
        id: LOCAL_PROFILE_ID
      }

      await addSystemScopeFilterProfile(newStagingProfile)
        .unwrap()
        .then(profileSaveSuccessCallback)
        .catch((err) => {
          /**
           * NOTE: This may be overwritten by RTKQ mutation effect handler
           * when response status is success
           *
           * Clear any saved variables that may have been set before the
           * server rejected the create (eg. partial savedFilter state).
           * Do NOT touch UI inputs/stagingProfile so the user can retry.
           */
          if (err.status !== 403) {
            setActionBarText('Profile save error')
          }

          dispatch(setEditInProgressSync(true))
        })
    } else {
      // UPDATE of existing profile
      updateProfileAPICallWrapper(stagingProfile)
    }
  }
  // #endRegion

  const canApplyProfile = (): boolean => {
    let canApply = true

    if ((stagingProfile?.continuousRoutes?.length ?? 0) < 1) {
      // REVIEW: Make this a validation type and set it in LineSelector
      canApply = false
    } else if (hasValidationErrors()) {
      canApply = false
    }

    return canApply
  }

  /**
   * Apply the current staging profile filter
   *
   * If staging profile contains unsaved changes, save to "unsaved profile"
   * and set "unsaved profile" as current active filter profile.
   * Otherwise, apply the selected profile as active filter profile.
   */
  const applyProfileCallback = async (): Promise<void> => {
    if (stagingProfile == null) {
      console.warn('Apply occured while SP is nullish')

      return
    }

    if (hasValidationErrors()) {
      console.warn('System scope validation error detected on apply')

      return
    }

    // editInProgress => apply profile with unsaved content
    if (editInProgress) {
      setActionBarText('Applying...')

      // REVIEW: what if filterProfiles is still loading?
      const unsavedProfileId = getUnsavedProfile(filterProfiles)?.id

      if (unsavedProfileId == null) {
        // Create first instance of "unsaved profile"
        console.trace('System Scope - Initializing "Unsaved Profile"')

        const systemScopeParams: IFilterProfile = {
          ...stagingProfile,
          name: UNSAVED_PROFILE_NAME,
          id: LOCAL_PROFILE_ID,
          isPrivate: true,
          isDefault: false,
          userId: currentUserId || 'unitialized'
        }

        await addSystemScopeFilterProfile(systemScopeParams)
          .unwrap()
          .then(profileApplySuccessCallback)
          .catch((err) => {
            if (err.status !== 403) {
              setActionBarText('Profile save error')
            }

            dispatch(setEditInProgressSync(true))
          })
      } else {
        // Replace unsaved profile with current changes
        const systemScopeParams: IFilterProfile = {
          ...stagingProfile,
          name: UNSAVED_PROFILE_NAME,
          id: unsavedProfileId,
          isPrivate: true,
          isDefault: false,
          userId: currentUserId || 'unitialized'
        }

        await updateSystemscopeFilterProfile(systemScopeParams)
          .unwrap()
          .then(profileApplySuccessCallback)
          .catch((err) => {
            /**
             * NOTE: This may be overwritten by RTKQ mutation effect handler
             * when response status is success
             */
            if (err.status !== 403) {
              setActionBarText('Profile save error')
            }

            dispatch(setEditInProgressSync(true))
          })
      }
    } else {
      profileApplySuccessCallback(stagingProfile)
    }
  }
  // #endregion

  const handleCloseChild = useCallback(
    (_event: object, reason: string): void => {
      if (reason === 'backdropClick') {
        return
      }

      setShowConfirmationModal(false)
    },
    [setShowConfirmationModal]
  )

  return (
    <Modal
      open={true}
      onClose={dismissModal}
      aria-labelledby='Global Filter Modal'
      className='flex items-center justify-center'
    >
      <div
        id='system-scope-modal'
        className='flex h-[min(46rem,96vh)] w-[min(74rem,96vw)] flex-col shadow-modal-box-shadow'
      >
        <Modal
          open={showConfirmationModal}
          onClose={handleCloseChild}
          className='flex items-center justify-center'
        >
          <div className='w-96 bg-white p-5'>
            <div className='mb-4'>
              Saving a System Scope profile as public will make it available to
              your entire network and cannot be modified.
            </div>
            <div className='float-right'>
              <button
                type='button'
                className='w-24 font-semibold uppercase text-gray-700'
                onClick={() => setShowConfirmationModal(false)}
              >
                Cancel
              </button>
              <button
                type='button'
                className={[
                  'p-1',
                  'w-24',
                  'rounded bg-lighthouseBlue',
                  'text-white',
                  'font-semibold',
                  'uppercase',
                  'disabled:opacity-50'
                ].join(' ')}
                onClick={() => {
                  saveProfileCallback()
                  setShowConfirmationModal(false)
                  setInitiallyPrivate(false)
                }}
              >
                Save
              </button>
            </div>
          </div>
        </Modal>
        <div
          className={[
            'flex justify-between bg-blue-navy',
            'rounded-t px-6 py-4 text-white'
          ].join(' ')}
        >
          <span className='text-2xl font-medium'>System Scope</span>
          <button type='button' onClick={closeModal}>
            <Close />
          </button>
        </div>
        <div className='flex min-h-0 flex-1 rounded-b bg-white'>
          <ProfileSelector
            onCreate={addProfileCallback}
            onSelect={selectProfileCallback}
            onDelete={deleteProfileCallback}
          />
          <div className='flex min-w-0 flex-1 flex-col'>
            <LoadingSpinner isLoading={isCreatingProfile || isUpdatingProfile}>
              <div className='flex h-full w-full'>
                <ProfileDetails
                  latestProfileSelectionTimestamp={Math.max(
                    latestNewProfileSelectionTimestamp,
                    stagingProfileAPIRequestStatus.latestSuccessTimestamp ?? -1
                  )}
                  setSearchValue={setSearchValue}
                  initiallyPrivate={initiallyPrivate}
                  setInitiallyPrivate={setInitiallyPrivate}
                  setOnlyShowSelected={setOnlyShowSelected}
                  setIsLoadingContinuousRoutes={setIsLoadingContinuousRoutes}
                />
                <LineSelector
                  isLoading={isLineSelectorLoading}
                  searchValue={searchValue}
                  setSearchValue={setSearchValue}
                  onlyShowSelected={onlyShowSelected}
                  setOnlyShowSelected={setOnlyShowSelected}
                />
              </div>
            </LoadingSpinner>
            <ActionBar
              message={actionBarText}
              canApply={canApplyProfile()}
              onReset={resetProfileCallback}
              onSave={onSaveProfile}
              onApply={applyProfileCallback}
              onClose={closeModal}
            />
          </div>
        </div>
      </div>
    </Modal>
  )
}

interface IFilterModalActionBar {
  message: string
  canApply: boolean
  onReset: () => void
  onSave: () => void
  onApply: () => void
  onClose: () => void
}

const ActionBar = ({
  message,
  canApply,
  onReset,
  onSave,
  onApply,
  onClose
}: IFilterModalActionBar) => {
  const { currentData: currentUserId } = useGetCurrentUserIdQuery()
  const stagingProfile = useAppSelector(selectStagingProfile)

  const isUserOwnedProfile =
    stagingProfile != null && stagingProfile.userId === currentUserId

  const canSaveProfile =
    (stagingProfile?.continuousRoutes.length ?? 0) > 0 && isUserOwnedProfile

  return (
    <div className='flex justify-between border-t-2 border-gray-200 py-3 pl-3 pr-6'>
      <div>
        <button
          type='button'
          className={[
            'mx-3 mb-3 mt-2.5 tracking-[0.02em]',
            canSaveProfile ? 'text-lighthouseBlue' : 'text-gray-300',
            'font-semibold uppercase'
          ].join(' ')}
          onClick={onSave}
          disabled={!canSaveProfile}
        >
          Save Profile
        </button>
        <button
          type='button'
          className={[
            'mx-3 mb-3 mt-2.5 tracking-[0.02em]',
            isUserOwnedProfile ? 'text-gray-700' : 'text-gray-300',
            'font-semibold uppercase'
          ].join(' ')}
          onClick={onReset}
          disabled={!isUserOwnedProfile}
        >
          Reset All
        </button>
      </div>
      <div className='flex'>
        <span className='m-3 px-3 text-gray-500'>{message}</span>
        <div className='grid grid-cols-2 gap-2'>
          <button
            type='button'
            className='bg-gray-200 px-6 py-2.5 font-semibold uppercase tracking-[0.02em]'
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type='button'
            className={[
              'px-6 py-2.5',
              'bg-lighthouseBlue',
              'text-white',
              'font-semibold',
              'uppercase',
              'disabled:opacity-50',
              'tracking-[0.02em]'
            ].join(' ')}
            disabled={!canApply}
            onClick={onApply}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  )
}

export default FilterModal
